import React from "react";
import { EmailTemplate } from "@common/components/email-template";
import { IPatientInviteFlow, PatientInviteFlowProps, PatientInviteStep, PatientInviteStepConfig } from "./types";
import { useTemplateController } from "./template";
import { I18n } from "react-redux-i18n";
import { QueryChooser } from "../components/query-chooser";
import { QueryControl, QueryControlProps } from "../components/query-control";
import { FormatFieldItem } from "@common/components/format-field-selector";
import { FhqInviteIndividualsGetParams } from "@modules/invite-status/services/invites";
import { UpdateDataDto } from "@common/dto/data/update-dto";
import { InviteHistoryDto } from "@common/dto/invite-history/list-dto";
import { InviteListDto } from "@common/dto/invite/list-dto";
import { InviteStatusIndividual, InviteStatusIndividualListDto } from "@modules/invite-status/dto/individuals/list-dto";
import { SkipLogicCondition } from "@common/components/skip-logic/skip-logic.types";
import { QueryManagementFormat } from "@common/components/query-management";
import { QueryManagementQueryFormatGetDto } from "@modules/invite-status/services/formats";
import { isEmail } from "@common/utils/email";
import { QuestionnaireType } from "@common/dto/questionnaire/questionnaire-type";
import { HttpErrorDto } from "@common/dto/http";
import { RelativeChooser } from "../components/fhq-invite-relative-chooser";
import { InviteDatasheetAdditionalDataUpdateDto } from "@common/dto/invite/datasheet/additional-data-update-dto";
import { InviteMetadataListDto } from "@modules/invite-status/dto/invite/new-invite-check/list-dto";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { MessageProps } from "@common/components/MessageBox.types";

export type PatientsSpecificCriteriaBasicProps = {
  updateIndividual: (data: UpdateDataDto) => Promise<void>;
  createFormatFolder: QueryControlProps["createFormatFolder"];
  deleteFormat: QueryControlProps["deleteFormat"];
  deleteFormatFolder: QueryControlProps["deleteFormatFolder"];
  getFolders: QueryControlProps["getFolders"];
  getFormatFolders: QueryControlProps["getFormatFolders"];
  getFormats: QueryControlProps["getFormats"];
  getFields: QueryControlProps["getFields"];
  showInfoMessage: QueryControlProps["showInfoMessage"];
  showErrorMessage: QueryControlProps["showErrorMessage"];
  getIndividualHistoryLogs: (params: {
    inviteId?: InviteListDto["id"];
    globalId: InviteStatusIndividual["id"];
    fhq4?: InviteListDto["fhq4"];
  }) => Promise<Array<InviteHistoryDto>>;
  getQueryFormat: (formatId: FormatFieldItem["id"]) => Promise<QueryManagementQueryFormatGetDto>;
  saveQueryFormats: (params: {
    format: Partial<QueryManagementFormat>;
    queries: Array<SkipLogicCondition>;
  }) => Promise<void>;
  saveAdditionalDatasheetFormatData: (data: InviteDatasheetAdditionalDataUpdateDto) => Promise<void>;
  getFhqInviteIndividuals: (params: FhqInviteIndividualsGetParams) => Promise<InviteStatusIndividualListDto>;
  getInviteMetadata: (params: { familyId: number; globalId: string }) => Promise<InviteMetadataListDto>;
  showConfirmMessage: (props: MessageProps) => void;
};

export type PatientsSpecificCriteriaProps = PatientInviteFlowProps & PatientsSpecificCriteriaBasicProps;

export const usePatientsSpecificCriteria = (props: PatientsSpecificCriteriaProps): IPatientInviteFlow => {
  const {
    showErrorMessage,
    onSubmit,
    getIndividualHistoryLogs,
    setCurrentStep,
    questionnaire,
    createFormatFolder,
    deleteFormat,
    deleteFormatFolder,
    getFolders,
    getFormats,
    showInfoMessage,
    getFields,
    getQueryFormat,
    saveQueryFormats,
    getFormatFolders,
    saveAdditionalDatasheetFormatData,
    getFhqInviteIndividuals,
    getInviteMetadata,
    showConfirmMessage,
  } = props;
  const { emailTemplate, emailSubject, setEmailSubject, setEmailTemplate } = useTemplateController();
  const [selectedFormat, setSelectedFormat] = React.useState<Partial<FormatFieldItem> | undefined>(undefined);
  const [selectedFolder, setSelectedFolder] = React.useState<FolderPrimaryListDto | undefined>(undefined);
  const [queries, setQueries] = React.useState<Array<SkipLogicCondition>>([]);

  const [selectedIndividuals, setSelectedIndividuals] = React.useState<Array<InviteStatusIndividual>>([]);

  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    setEmailSubject(questionnaire?.emailTemplate.subject || "");
    setEmailTemplate(questionnaire?.emailTemplate.body || "");
  }, [questionnaire]);

  const handleSelectedFolderChange = React.useCallback(({ folder }: { folder: FolderPrimaryListDto }) => {
    setSelectedFolder(folder);
    setSelectedFormat((prev) => ({ ...prev, folderId: folder.id } as FormatFieldItem));
  }, []);

  const handleSelectedFormatChange = React.useCallback(({ format }: { format: Partial<FormatFieldItem> }) => {
    setSelectedFormat(format);
  }, []);

  const resetSelectedFormat = React.useCallback(() => setSelectedFormat(undefined), []);

  const handleSaveQueryFormats = React.useCallback(
    (params: { format: Partial<QueryManagementFormat>; queries: Array<SkipLogicCondition> }): Promise<void> => {
      const canUpdateExistsFormat =
        params.format?.folderId?.toString() === selectedFormat?.folderId?.toString() &&
        params.format?.id?.toString() === selectedFormat?.id?.toString() &&
        selectedFormat?.id !== undefined;

      return new Promise((resolve) => {
        const submitFormat = () => {
          const format = {
            name: params.format.name,
            description: params.format.description,
            clauses: params.queries,
            id: canUpdateExistsFormat ? params.format.id : undefined,
            folderId: selectedFormat?.folderId as string,
          };
          return saveQueryFormats({
            format,
            queries: params.queries,
          })
            .then(() => {
              setSelectedFormat(undefined);
              setSelectedFolder(undefined);
            })
            .finally(() => resolve());
        };

        if (canUpdateExistsFormat) {
          showConfirmMessage({
            cancelText: I18n.t("No"),
            confirmText: I18n.t("Yes"),
            confirmBtnId: "btn-message-box-ok",
            cancelBtnId: "btn-message-box-dont-cancel",
            content: I18n.t("alreadyExistsFormatInTheSpecifiedFolder")
              .replace("{oldFormatName}", params.format.name!)
              .replace("{newFormatName}", selectedFormat.name!),
            onCustomOk: submitFormat,
          });
        } else {
          return submitFormat();
        }
      });
    },
    [saveQueryFormats, selectedFormat, showConfirmMessage]
  );

  const stepsConfig: Partial<Record<PatientInviteStep, PatientInviteStepConfig>> = {
    [PatientInviteStep.queryChooser]: {
      component: (
        <QueryChooser
          onNewFormat={() => {
            setCurrentStep(PatientInviteStep.queryFormatManagement);
            setQueries([]);
          }}
          selectedFormat={selectedFormat as FormatFieldItem}
          showErrorMessage={showErrorMessage}
          getFolders={getFormatFolders}
          getFormats={getFormats}
          onSelectedFormatChange={setSelectedFormat}
        />
      ),
      onGoToPreviousStep: () => {
        setSelectedFormat(undefined);
      },
      previousStep: PatientInviteStep.receiverChooser,
      onSubmit: () => {
        setCurrentStep(PatientInviteStep.queryFormatManagement);
      },
    },
    [PatientInviteStep.queryFormatManagement]: {
      component: (
        <QueryControl
          getQueryFormat={selectedFormat?.id ? () => getQueryFormat(selectedFormat.id!) : undefined}
          submitText={I18n.t("Save")}
          submitBtnDisabled={
            selectedFormat?.folderId === undefined || !selectedFormat?.name?.trim().length || !selectedFolder
          }
          queries={queries}
          onQueriesChange={setQueries}
          createFormatFolder={createFormatFolder}
          deleteFormat={deleteFormat}
          deleteFormatFolder={deleteFormatFolder}
          getFolders={getFolders}
          getFormatFolders={getFormatFolders}
          getFormats={getFormats}
          getFields={getFields}
          showInfoMessage={showInfoMessage}
          showErrorMessage={showErrorMessage}
          saveQueryFormats={handleSaveQueryFormats}
          onSelectedFolderChange={handleSelectedFolderChange}
          onSelectedFormatChange={handleSelectedFormatChange}
          formatNameSearchPlaceholder={I18n.t("Enter New Format Name")}
          formatDescriptionSearchPlaceholder={I18n.t("Enter New Format Description")}
          onQueryManagementClose={resetSelectedFormat}
        />
      ),
      onGoToPreviousStep: () => {
        setSelectedFormat(undefined);
      },
      previousStep: PatientInviteStep.queryChooser,
      onSubmit: () => {
        const isNotValidQuery = queries.some((query) => {
          return [!!query.value?.trim().length, query.operator, query.leftParenthesis === query.rightParenthesis].some((condition) => !condition);
        });

        if (isNotValidQuery) {
          showErrorMessage(`${I18n.t("Query model is invalid")}. ${I18n.t("Please review")}`);
          return;
        }

        setCurrentStep(PatientInviteStep.individualChooser);
      },
    },
    [PatientInviteStep.individualChooser]: {
      submitBtnProps: {
        disabled: !selectedIndividuals.length,
      },
      component: questionnaire && (
        <RelativeChooser
          saveAdditionalDatasheetFormatData={saveAdditionalDatasheetFormatData}
          questionnaire={questionnaire}
          showErrorMessage={showErrorMessage}
          selectedIndividuals={selectedIndividuals}
          getHistoryLogs={getIndividualHistoryLogs}
          getFhqInviteRelatives={(params: { start: number }) =>
            getFhqInviteIndividuals({
              id: questionnaire.questionnaireId,
              fhq4: questionnaire.fhq4,
              start: params.start,
              length: 50,
              format: {
                id: selectedFormat?.id,
                name: selectedFormat?.name,
                description: selectedFormat?.description,
                folderId: selectedFormat?.folderId as string,
                clauses: queries.map(({ id, ...item }) => item),
              },
            })
          }
          onChange={setSelectedIndividuals}
        />
      ),
      onGoToPreviousStep: () => {
        setSelectedIndividuals([]);
      },
      previousStep: PatientInviteStep.queryFormatManagement,
      submitting,
      onSubmit: async () => {
        const validations = [
          {
            isValid: (selectedIndividual: InviteStatusIndividual) =>
              selectedIndividual.firstName && selectedIndividual.lastName && selectedIndividual.emailAddress,
            msg: I18n.t("Please provide first name, last name and email"),
          },
          {
            isValid: (selectedIndividual: InviteStatusIndividual) => isEmail(selectedIndividual.emailAddress),
            msg: I18n.t("Please provide a valid email"),
          },
        ];

        if (!selectedIndividuals.length) {
          return;
        }

        let invalidValidation:
          | { msg: string; isValid: (selectedIndividual: InviteStatusIndividual) => boolean }
          | undefined;

        selectedIndividuals.find((selectedIndividual) => {
          invalidValidation = validations.find((v) => !v.isValid(selectedIndividual));
          return invalidValidation;
        });

        if (invalidValidation) {
          showErrorMessage(invalidValidation.msg);

          return;
        }

        try {
          setSubmitting(true);

          const isNotValidIndividuals: Array<boolean> = [];

          if (questionnaire?.questionnaireType === QuestionnaireType.family) {
            await Promise.all(
              selectedIndividuals.map(async (selectedIndividual) => {
                const familyData = await getInviteMetadata({
                  familyId: selectedIndividual.familyId,
                  globalId: selectedIndividual.id,
                });

                isNotValidIndividuals.push(
                  familyData.some((data) => !data.proband && !data.singlet && data.pedigreeMemberCount != 1)
                );
              })
            );
          }

          if (isNotValidIndividuals.some((isNotValid) => isNotValid)) {
            showErrorMessage(
              `${I18n.t(
                "You cannot invite some of selected individual(s) to a Family History Questionnaire"
              )}. ${I18n.t("selectIndividualForSendInvite")}. ${I18n.t(
                "This problem applies to the following individual(s)"
              )}`
            );
            return;
          }
          setCurrentStep(PatientInviteStep.emailTemplate);
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Failed to check individual"));
        } finally {
          setSubmitting(false);
        }
      },
    },
    [PatientInviteStep.emailTemplate]: {
      component: (
        <EmailTemplate
          subject={emailSubject}
          onSubjectChange={setEmailSubject}
          template={emailTemplate}
          onTemplateChange={setEmailTemplate}
        />
      ),
      submitBtnProps: {
        text: I18n.t("Send an Invite"),
      },
      previousStep: PatientInviteStep.individualChooser,
      onSubmit: async () => {
        if (!selectedIndividuals.length || !questionnaire) {
          return;
        }

        try {
          setSubmitting(true);
          await onSubmit({
            globalIds: selectedIndividuals.map((individual) => individual.id),
            fhq4: questionnaire.fhq4,
            questionnaireId: questionnaire.questionnaireId,
            emailSubject,
            emailBody: emailTemplate,
          });
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Failed to send an invite"));
        } finally {
          setSubmitting(false);
        }
      },
    },
  };

  return {
    stepsConfig,
    getFirstStep: () => PatientInviteStep.queryChooser,
  };
};
